import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { ApiEndpointService } from './common/api-endpoint.service';

@Injectable({ providedIn: 'root' })
export class AppService {

  constructor(
    private http: HttpClient,
    private apiEndpoint: ApiEndpointService
  ) { }

  // Build the full URL from the server-provided base
  private url(method: string) {
    console.log('[AppService] - calling api endpoint: ', this.apiEndpoint.buildUrl(`/api/App/${method}`))
    return this.apiEndpoint.buildUrl(`/api/App/${method}`);
  }

  public GetDentalBidirectionalLists(request: GetDentalBidirectionalListsRequest):
    Observable<GetDentalBidirectionalListsResponse> {
    // If request is meant to be query params, convert to HttpParams; otherwise keep as options
    return this.http.get<GetDentalBidirectionalListsResponse>(
      this.url('GetDentalBidirectionalLists'),
      { withCredentials: true }
    );
  }

  public GetMemberData(request: GetMemberDataRequest): Observable<GetMemberDataResponse> {
    const params = new HttpParams().set('memberId', request.memberId);
    return this.http.get<GetMemberDataResponse>(this.url('GetMemberData'), { params, withCredentials: true });
  }

  public SavePCPReferral(request: SavePCPReferralRequest): Observable<BaseMessage> {
    return this.http.post<BaseMessage>(this.url('SavePCPReferral'), request, { withCredentials: true });
  }

  public GetDCOAdmins(_: GetDCOAdminsRequest): Observable<GetDCOAdminsResponse> {
    return this.http.get<GetDCOAdminsResponse>(this.url('GetDCOAdmins'), { withCredentials: true });
  }

  public SaveDCO(request: SaveDCORequest): Observable<BaseMessage> {
    return this.http.post<BaseMessage>(this.url('SaveDCO'), request, { withCredentials: true });
  }

  public GetRuntimeAppSettings(request: GetRuntimeAppSettingsRequest):
    Observable<GetRuntimeAppSettingsResponse> {
    const params = new HttpParams().set('name', request.name);
    return this.http.get<GetRuntimeAppSettingsResponse>(this.url('GetRuntimeAppSettings'), { params, withCredentials: true });
  }

  public SaveRuntimeAppSetting(request: SaveRuntimeAppSettingRequest):
    Observable<SaveRuntimeAppSettingResponse> {
    return this.http.post<SaveRuntimeAppSettingResponse>(this.url('SaveRuntimeAppSetting'), request, { withCredentials: true });
  }

  public GetDCOs(request: GetDCOsRequest): Observable<GetDCOsResponse> {
    const params = new HttpParams().set('activeOnly', request.activeOnly);
    return this.http.get<GetDCOsResponse>(this.url('GetDCOs'), { params, withCredentials: true });
  }

  public GetDentalReferralsByUser(request: GetDentalReferralsByUserRequest):
    Observable<GetDentalReferralsByUserResponse> {
    const params = new HttpParams().set('includeClosedReferrals', request.includeClosedReferrals);
    return this.http.get<GetDentalReferralsByUserResponse>(this.url('GetDentalReferralsByUser'), { params, withCredentials: true });
  }

  public GetDentalReferralsToReprocess(request: GetDentalReferralsToReprocessRequest):
    Observable<GetDentalReferralsToReprocessResponse> {
    const params = new HttpParams().set('includeClosedReferrals', request.includeClosedReferrals);
    return this.http.get<GetDentalReferralsToReprocessResponse>(this.url('GetDentalReferralsToReprocess'), { params, withCredentials: true });
  }

  public GetUserDco(request: GetUserDcoRequest): Observable<GetUserDcoResponse> {
    const params = new HttpParams().set('userId', request.userId);
    return this.http.get<GetUserDcoResponse>(this.url('GetUserDco'), { params, withCredentials: true });
  }

  public SaveDentalReferralReview(request: SaveDentalReferralReviewRequest):
    Observable<SaveDentalReferralReviewResponse> {
    return this.http.post<SaveDentalReferralReviewResponse>(this.url('SaveDentalReferralReview'), request, { withCredentials: true });
  }

  public SaveDentalReferralReviews(request: SaveDentalReferralReviewsRequest):
    Observable<SaveDentalReferralReviewsResponse> {
    return this.http.post<SaveDentalReferralReviewsResponse>(this.url('SaveDentalReferralReviews'), request, { withCredentials: true });
  }

  public SaveDentalBidirectionalListItem(request: SaveDentalBidirectionalListItemRequest):
    Observable<SaveDentalBidirectionalListItemResponse> {
    return this.http.post<SaveDentalBidirectionalListItemResponse>(this.url('SaveDentalBidirectionalListItem'), request, { withCredentials: true });
  }

  public SaveListItemOrder(request: SaveListItemOrderRequest): Observable<SaveListItemOrderResponse> {
    return this.http.post<SaveListItemOrderResponse>(this.url('SaveListItemOrder'), request, { withCredentials: true });
  }

  public GetReferralReviewNotes(request: GetReferralReviewNotesRequest):
    Observable<GetReferralReviewNotesResponse> {
    const params = new HttpParams().set('referralReviewId', request.referralReviewId);
    return this.http.get<GetReferralReviewNotesResponse>(this.url('GetReferralReviewNotes'), { params, withCredentials: true });
  }

  public SaveReferralReviewNote(request: SaveReferralReviewNoteRequest):
    Observable<SaveReferralReviewNoteResponse> {
    return this.http.post<SaveReferralReviewNoteResponse>(this.url('SaveReferralReviewNote'), request, { withCredentials: true });
  }

  public DeleteReferralReviewNote(request: DeleteReferralReviewNoteRequest):
    Observable<DeleteReferralReviewNoteResponse> {
    return this.http.post<DeleteReferralReviewNoteResponse>(this.url('DeleteReferralReviewNote'), request, { withCredentials: true });
  }

  public UpdatePCPReferralReprocessState(request: UpdatePCPReferralReprocessStateRequest):
    Observable<UpdatePCPReferralReprocessStateResponse> {
    return this.http.post<UpdatePCPReferralReprocessStateResponse>(this.url('UpdatePCPReferralReprocessState'), request, { withCredentials: true });
  }

  public UpdateReferralReprocessState(request: UpdateReferralReprocessStateRequest):
    Observable<UpdateReferralReprocessStateResponse> {
    return this.http.post<UpdateReferralReprocessStateResponse>(this.url('UpdateReferralReprocessState'), request, { withCredentials: true });
  }

  public ReprocessReferral(request: ReprocessReferralRequest): Observable<ReprocessReferralResponse> {
    return this.http.post<ReprocessReferralResponse>(this.url('ReprocessReferral'), request, { withCredentials: true });
  }

  public GetMemberEnrollmentHistory(request: GetMemberEnrollmentHistoryRequest):
    Observable<GetMemberEnrollmentHistoryResponse> {
    const params = new HttpParams().set('memberId', request.memberId);
    return this.http.get<GetMemberEnrollmentHistoryResponse>(this.url('GetMemberEnrollmentHistory'), { params, withCredentials: true });
  }

  public GetReferralHistory(request: GetReferralHistoryRequest):
    Observable<GetReferralHistoryResponse> {
    return this.http.post<GetReferralHistoryResponse>(this.url('GetReferralHistory'), request, { withCredentials: true });
  }

  public SearchMember(request: SearchMemberRequest): Observable<SearchMemberResponse> {
    return this.http.post<SearchMemberResponse>(this.url('SearchMember'), request, { withCredentials: true });
  }

  public GetMemberDentalPlans(request: GetMemberDentalPlansRequest):
    Observable<GetMemberDentalPlansResponse> {
    return this.http.post<GetMemberDentalPlansResponse>(this.url('GetMemberDentalPlans'), request, { withCredentials: true });
  }

  public UpdateReferralMemberAssignment(request: UpdateReferralMemberAssignmentRequest):
    Observable<UpdateReferralMemberAssignmentResponse> {
    return this.http.post<UpdateReferralMemberAssignmentResponse>(this.url('UpdateReferralMemberAssignment'), request, { withCredentials: true });
  }

  public GetUsers(_: GetUsersRequest): Observable<GetUsersResponse> {
    return this.http.get<GetUsersResponse>(this.url('GetUsers'), { withCredentials: true });
  }

  public GetUserById(request: GetUserByIdRequest): Observable<GetUserByIdResponse> {
    const params = new HttpParams().set('UserId', request.userId);
    return this.http.get<GetUserByIdResponse>(this.url('GetUserById'), { params, withCredentials: true });
  }

  public UpdateUser(request: UpdateUserRequest): Observable<UpdateUserResponse> {
    return this.http.put<UpdateUserResponse>(this.url('UpdateUser'), request, { withCredentials: true });
  }

  public GetDentalBidirectionalRoles(_: GetDentalBidirectionalRolesRequest):
    Observable<GetDentalBidirectionalRolesResponse> {
    return this.http.get<GetDentalBidirectionalRolesResponse>(this.url('GetDentalBidirectionalRoles'), { withCredentials: true });
  }

  public GetReferralByReferralId(request: GetReferralByReferralIdRequest):
    Observable<GetReferralByReferralIdResponse> {
    return this.http.post<GetReferralByReferralIdResponse>(this.url('GetReferralByReferralId'), request, { withCredentials: true });
  }

  public SearchUserSessions(request: SearchUserSessionsRequest):
    Observable<SearchUserSessionsResponse> {
    return this.http.post<SearchUserSessionsResponse>(this.url('SearchUserSessions'), request, { withCredentials: true });
  }
  
  public GetDcosLite(activeOnly = true): Observable < DcoLite[] > {
  const params = new HttpParams().set('activeOnly', String(activeOnly));
  return this.http
    .get<any>(this.url('GetDCOs'), { params, withCredentials: true })
    .pipe(
      map(resp => {
        // Accept multiple common shapes
        if (Array.isArray(resp)) return resp;
        if (Array.isArray(resp?.items)) return resp.items;
        if (Array.isArray(resp?.dcos)) return resp.dcos;
        if (Array.isArray(resp?.data)) return resp.data;
        return []; // fallback
      })
    );
  }

  public GetAllUsers(): Observable<DentalBidirectionalUserLite[]> {
    return this.http.get<DentalBidirectionalUserLite[]>(
      this.url('GetAllUsers'),
      { withCredentials: true }
    );
  }

  public GetAllReferralsByMember(request: GetAllReferralsByMemberRequest):
    Observable<GetAllReferralsByMemberResponse> {
    const params = new HttpParams().set('memberMedicaidId', request.memberMedicaidId);
    return this.http.get<GetAllReferralsByMemberResponse>(this.url('GetAllReferralsByMember'), { params, withCredentials: true });
  }

  
  public BackfillMissingCcos(): Observable<any> {
    return this.http.post(this.url('BackfillMissingCcos'), { withCredentials: true });
  }

}


export class BaseMessage {
  public success: boolean;
  public message: string;
}


export class GetAllReferralsByMemberResponse extends BaseMessage {
  referrals: ReferralSummary[];
}

export class GetAllReferralsByMemberRequest {
  memberMedicaidId: string;
}

export class SearchUserSessionsRequest {
  startUtc: string;         // ISO (UTC)
  endUtc: string;           // ISO (UTC)
  dcoId?: number | null;
  userQuery?: string | null;
  activeOnly?: boolean | null;
  authSource?: string | null;

  page: number;
  pageSize: number;
  sort?: string | null;     // e.g. 'createdUtc', 'lastSeenUtc', 'email', ...
  dir?: 'asc' | 'desc' | null;
}

export class SearchUserSessionsResponse extends BaseMessage {
  total: number;
  items: UserSession[];
}

export class GetReferralByReferralIdRequest {
  referralId: number;
  referralType: string;
}
export class GetReferralByReferralIdResponse extends BaseMessage {
  referral: DentalReferralForm;
}

export class GetDentalBidirectionalRolesRequest { }
export class GetDentalBidirectionalRolesResponse extends BaseMessage
{
  roles: DentalBidirectionalRole[] = [];
}

export class GetUsersRequest { }

export class GetUsersResponse extends BaseMessage {
  users: DentalBidirectionalUser[] = [];
}

export class GetUserByIdRequest {
  userId: string = '';
}

export class GetUserByIdResponse extends BaseMessage {
  user!: DentalBidirectionalUser;
}

export class UpdateUserRequest {
  user!: DentalBidirectionalUser;
}

export class UpdateUserResponse extends BaseMessage {
  user!: DentalBidirectionalUser;
}


export class UpdateReferralMemberAssignmentRequest {
  referral?: DentalReferralForm;
  sendToDCO?: boolean;
  manualUpdate?: boolean;
}

export class UpdateReferralMemberAssignmentResponse extends BaseMessage {
  memberDCO?: DCO;
}

export class SearchMemberRequest {
  searchMemberParameters?: MemberSearchParameters;
}

export class SearchMemberResponse extends BaseMessage {
  searchMemberResults?: MemberSearchParameters[];
}

export class GetMemberDentalPlansRequest {
  memberId?: string;
}

export class GetMemberDentalPlansResponse extends BaseMessage {
  memberDentalPlans?: MemberCCOEnrollment[];
}

export class GetReferralHistoryRequest {
  referralId!: number;
  referralType!: string;
}

export class GetReferralHistoryResponse extends BaseMessage {
  versionChanges!: VersionChanges[]; 
}
export class GetMemberEnrollmentHistoryRequest {
  memberId!: string;
}

export class GetMemberEnrollmentHistoryResponse extends BaseMessage {
  enrollments!: MemberCCOEnrollment[];
}

export class ReprocessReferralRequest {
  referral: DentalReferralForm;
}

export class ReprocessReferralResponse extends BaseMessage {
  memberDCO!: DCO;
  public member: MemberCCOEnrollment;
  public isMemberActive: boolean;
  public isMemberFound: boolean;
}

export class UpdateReferralReprocessStateRequest {
  referral!: DentalReferralForm;
}

export class UpdateReferralReprocessStateResponse extends BaseMessage {

}

export class UpdatePCPReferralReprocessStateRequest {
  pcpReferral!: PCPReferral;
}

export class UpdatePCPReferralReprocessStateResponse extends BaseMessage {
  
}
export class DeleteReferralReviewNoteRequest {
  noteId!: number;
}

export class DeleteReferralReviewNoteResponse extends BaseMessage {
  notes!: DentalReferralFormReviewNote[];
}
export class GetReferralReviewNotesRequest {
  referralReviewId!: number;
}

export class GetReferralReviewNotesResponse extends BaseMessage {
  notes!: DentalReferralFormReviewNote[];
}

export class SaveReferralReviewNoteRequest {
  note: DentalReferralFormReviewNote;
  referralId!: number;
  referralType!: string;
}

export class SaveReferralReviewNoteResponse extends BaseMessage {
  notes!: DentalReferralFormReviewNote[];
}
export class SaveListItemOrderResponse extends BaseMessage {

}
export class SaveListItemOrderRequest {
  public ListItems: DentalBidirectionalListItem[];
}

export class SaveDentalBidirectionalListItemResponse extends BaseMessage {
  
}

export class SaveDentalBidirectionalListItemRequest {
  public listItem: DentalBidirectionalListItem;
}

export class SaveDentalReferralReviewResponse extends BaseMessage {
  
}

export class SaveDentalReferralReviewRequest {
  public review: DentalReferralFormReview;
}

export class SaveDentalReferralReviewsResponse extends BaseMessage {

}

export class SaveDentalReferralReviewsRequest {
  public reviews: DentalReferralFormReview[];
}

export class GetUserDcoResponse extends BaseMessage {
  dco: DCO;
}

export class GetUserDcoRequest {
  userId?: number;
}

export class GetDentalReferralsByUserResponse extends BaseMessage {
  referrals: DentalReferralForm[];
  dcoDisplayName?: string;
}

export class GetDentalReferralsByUserRequest {
  includeClosedReferrals: boolean;
}

export class GetDentalReferralsToReprocessResponse extends BaseMessage {
  pcpReferrals: PCPReferral[];
  hratReferrals: HRATReferral[];
  maternityReferrals: MaternityReferral[];
  referrals: DentalReferralForm[];
}

export class GetDentalReferralsToReprocessRequest {
  includeClosedReferrals: boolean;
}

export class GetDCOsResponse extends BaseMessage {
  dcos: DCO[];
}

export class GetDCOsRequest {
  activeOnly?: boolean;
}

export class GetRuntimeAppSettingsResponse extends BaseMessage {
  settings: RuntimeAppSetting[];
}

export class GetRuntimeAppSettingsRequest {
  name?: string;
}

export class SaveRuntimeAppSettingResponse extends BaseMessage {
  settings: RuntimeAppSetting[];
}

export class SaveRuntimeAppSettingRequest {
  setting: RuntimeAppSetting;
}

export class SaveDCORequest {
  public dcoToSave: DCO;
}

export class GetDCOAdminsResponse extends BaseMessage {
  public dcoAdmins: DCOAdmin[] = [];
  public dcosInSnowflake: SnowflakeDco[] = [];
}

export class GetDCOAdminsRequest {
  
}

export class GetMemberDataResponse extends BaseMessage {
  public member: MemberCCOEnrollment;
  public memberMasterDimLite: MemberMasterDimLite;
  public isMemberActive: boolean;
  public isMemberFound: boolean;
}

export class GetMemberDataRequest {
  public memberId: string = '';
}

export class GetDentalBidirectionalListsResponse extends BaseMessage {
  public lists: DentalBidirectionalList[] = [];
}
export class GetDentalBidirectionalListsRequest {

}

export class SavePCPReferralRequest {
  pcpReferral!: PCPReferral;
}

export enum AppSetting {
  IsAppEnabled = 'isAppEnabled',
  SystemAccountDCO = 'systemAccountDCO'
}


export class DentalBidirectionalList {
  public id: number;
  public name: string;
  public description: string;
  public createdBy?: DentalBidirectionalUser;
  public modifiedBy?: DentalBidirectionalUser;
  public createdDate: Date;
  public modifiedDate: Date;
  public listItems: DentalBidirectionalListItem[];
}

export class DentalBidirectionalListItem {
  public id?: number;
  public listId: number;
  public name: string;
  public description: string;
  public order: number;
  public active: boolean;
  public createdBy?: DentalBidirectionalUser;
  public modifiedBy?: DentalBidirectionalUser;
  public createdDate: Date;
  public modifiedDate: Date;
}

export class DentalBidirectionalUser {
  id?: number;
  userId?: string;
  externalUserId: string = '';
  firstName: string = '';
  lastName: string = '';
  emailAddress: string = '';
  dentalCareOrganizationId?: number;
  dentalCareOrganizationDisplayName?: string = '';
  isActive: boolean = false;
  roles: DentalBidirectionalRole[] = [];
  fullName?: string;
}

export class DentalBidirectionalRole {
  id!: number;
  name!: string;
  description!: string;
}

export class MemberCCOEnrollment {
  enrollId?: string;
  memId?: string;
  memberId?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  gender?: string;
  ethnicity?: string;
  dob?: Date;
  planCode?: string;
  planName?: string;
  planDescription?: string;
  enrollType?: string;
  cco?: string;
  rae?: string;
  php?: string;
  rateCd?: string;
  enrollStatus?: string;
  effectiveDate?: Date;
  terminationDate?: Date;
  isActive?: string;
  phoneNumber?: string;
}

export class DentalReferralForm {
  id?: number;
  dentalCareOrganizationId?: number;
  memberMedicaidId?: string;
  memberDob?: Date;
  memberFirstName?: string;
  memberLastName?: string;
  memberPhoneNumber?: string;
  memberMailingAddress1?: string;
  memberMailingAddress2?: string;
  memberMailingCity?: string;
  memberMailingState?: string;
  memberMailingZip?: string;
  memberMailingCounty?: string;
  memberLanguageSpoken?: string;
  memberLanguageWritten?: string;
  memberParentOrGuardianName?: string;
  referralType!: string;
  submittedOnDate?: Date;
  reprocess?: boolean;
  reprocessReason?: string;
  reprocessAdditionalDetails?: string;
  review?: DentalReferralFormReview;
  dco?: DCO;
  referralDate?: Date;
  cco?: string;
  receivedDate?: Date;
}

export class HRATReferral extends DentalReferralForm {
  providerHtrioId!: string;   
  //referralDate!: Date;        
  uniqueId?: string;          
  importDate?: Date;         
  importedDco?: string;       
  questions?: HRATReferralQuestion[];
}

export class HRATReferralQuestion {
  id?: number;         
  questionId!: number; 
  question!: string;   
  responseId!: number; 
  response?: string;   
}

export class MaternityReferral extends DentalReferralForm {
  providerHtrioId!: string;
  //referralDate!: Date;
  uniqueId?: string;
  importDate?: Date;
  importedDco?: string;
  referralReason?: string;
  additionalNotes?: string;
  clinic?: string;
  questions?: string;
  providerNpi?: string;
  patientAwareOfRequest?: string;
  swellingOrAbsess?: string;
  cardiovascularDisease?: string;
  comments?: string;
  dentalCarriesOrDecay?: string;
  diabetes?: string;
  edd?: string;
  hasOtherCondition?: boolean;
  otherConditions?: string;
  hasOtherPresentation?: boolean;
  otherPresentation?: string;
  oralPathology?: string;
  pain?: string;
  pregnancy?: string;
}

export class PCPReferral extends DentalReferralForm {
  
  isUrgent?: boolean;
  guardianName: string;
  clinicProgramName?: string;
  clinicNpi?: string;
  providerName?: string;
  providerPhoneNumber?: string;
  providerFaxNumber?: string;
  providerEmailAddress?: string;
  submittedBy?: string;
  dentalCariesOrDecay?: boolean;
  swellingOrAbscess?: boolean;
  oralPathology?: boolean;
  pain?: boolean;
  otherClinicalPresentation?: boolean;
  otherClinicalPresentationDescription?: string;
  pregnancy?: boolean;
  diabetes?: boolean;
  cardiovascularDisease?: boolean;
  otherMedicalConditions?: boolean;
  otherMedicalConditionsDescription?: string;
  commentsAboutCondition?: string;
  createdByUserId: string;
  createdDate: Date;
  modifiedByUserId: string;
  modifiedDate: Date;
}

export class DentalReferralFormReview {
  id?: number;
  referralId: number;
  spokeWithMember?: boolean;
  leftMessage?: boolean;
  sentLetter?: boolean;
  scheduledAppointment?: boolean;
  appointmentDate?: Date;
  dcoNote?: string;
  referralType: string;
  isClosed?: boolean;
  closedByUserId?: string;
  closedDate?: Date;
  isLocked?: boolean;
  lockedByUserId?: string;
  lockedDate?: Date;
  createdByUserId: string;
  createdDate: Date;
  modifiedByUserId: string;
  modifiedDate: Date;
  notes?: DentalReferralFormReviewNote[];
  closedByUser?: DentalBidirectionalUser;
  lockedByUser?: DentalBidirectionalUser;
}

export class DentalReferralFormReviewNote {
  id?: number;
  reviewId: number;
  note: string;
  createdByUserId: string;
  createdDate: Date;
  modifiedByUserId: string;
  modifiedDate: Date;
  createdByUser?: DentalBidirectionalUser;
  modifiedByUser?: DentalBidirectionalUser;
  editMode: boolean = false;
}


export class SnowflakeCco {
  name: string = '';
  activeMemberCount: number = 0;
}

export class SnowflakeDco {
  name: string = '';
  snowflakeCcos: SnowflakeCco[] = [];
  savedDco?: DCO;
  activeMemberCount: number = 0;
}

export class DCO {
  id?: number;
  name: string = '';
  displayName: string = '';
  description?: string;
  notificationEmail?: string;
  active: boolean = false;
  createdByUserId: string;
  createdDate: Date = new Date();
  modifiedByUserId: string;
  modifiedDate: Date = new Date();
  snowflakeCcos: SnowflakeCco[] = [];
  dcoName: string = '';
}

export class DCOAdmin {
  dentalCareOrganization?: DCO;
  snowflakeDco?: SnowflakeDco;
}

export class RuntimeAppSetting {
  id?: number;
  name: string;
  value: string;
  modifiedByUserId?: string;
  modifiedDate?: Date = new Date();
}

export class ChangedField {
  propertyName!: string;
  oldValue?: any;
  newValue?: any;
  changeEffectiveAt!: Date;
}

export class VersionChanges {
  versionStartTime!: Date;
  versionStartTimePst!: Date;
  changedFields: ChangedField[] = [];
}

export interface VersionChangesNode {
  name: string;                 // label for display
  children?: VersionChangesNode[];
}

export class MemberSearchParameters {
  dmapId?: string;
  memId?: string;
  ssn?: number;
  dob?: Date;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  cco?: string;
}

//export class MemberDimLite {
//  memberdimid?: number;
//  headofhousedmap?: string;
//  guardiandmap?: string;
//  primarylanguage?: string;
//  mailingaddress1?: string;
//  mailingaddress2?: string;
//  mailingcity?: string;
//  mailingstate?: string;
//  mailingzip?: string;
//  mailingcounty?: string;
//  languagespoken?: string;
//  languagewritten?: string;
//}

export class MemberMasterDimLite {
  memberDurableKey!: number;
  memberMedicaidId?: string;
  familyMedicaidId?: string;

  // Choose one primary display language (favor QNXT → OHA spoken → PH Tech)
  primaryLanguage?: string;

  // Mailing snippet for display
  mailingAddressLine1?: string;
  mailingAddressLine2?: string;
  mailingCity?: string;
  mailingStateCode?: string;
  mailingZipCode?: string;
  mailingCounty?: string;

  // spoken/reading
  languageSpoken?: string;
  languageWritten?: string;
}


export class UserSession {
  sessionId!: string;
  userId!: string;

  email!: string;
  firstName?: string | null;
  lastName?: string | null;

  dcoDisplay?: string | null;
  authSource!: string;

  createdUtc!: string;
  lastSeenUtc?: string | null;
  logoutUtc?: string | null;

  isTerminated!: boolean;

  ipAddress?: string | null;

  userAgent?: string | null;
  chUa?: string | null;
  chUaFullVersion?: string | null;
  chPlatform?: string | null;
  chPlatformVersion?: string | null;
  chMobile?: boolean | null;
}

// For filters
export interface DcoLite {
  id: number;
  displayName: string;
}

export interface DentalBidirectionalUserLite {
  userId: string;
  firstName?: string | null;
  lastName?: string | null;
  emailAddress?: string | null;
}

export interface ReferralSummary {
  sourceType: 'PCP' | 'HRAT' | 'Maternity';
  referralId: number;
  dcoId?: number | null;
  dcoName?: string | null;
  createdUtc: string;
  isUrgent: boolean;
  isClosed: boolean;
  isRecent: boolean;       // already computed by API
  isInReprocess: boolean;  // already computed by API
  isHidden: boolean;
}
